/**
 * Sélection et dépôt d'un fichier (docs/09 §2).
 *
 * Trois refus possibles, et le composant les distingue **avant** de transférer
 * quoi que ce soit :
 *
 * - le type, contre l'allowlist que le serveur rend pour cet usage ;
 * - la taille, contre `storage.max_file_mb` lu dans les droits ;
 * - la capacité elle-même, `storage.upload`, qui décide si le bouton existe.
 *
 * ⚠️ **Aucun de ces trois contrôles ne protège quoi que ce soit.** Les trois
 * sont refaits côté serveur, et chacun a son test d'API en face. Ils existent
 * pour éviter à l'utilisateur d'attendre la fin d'un transfert de 40 Mo avant
 * d'apprendre que son fichier ne convient pas — et le message qu'ils affichent
 * est celui d'un formulaire, pas celui d'une erreur.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEntitlements } from "@/features/billing/api/useBilling";
import { UpgradePrompt } from "@/features/billing/components/UpgradePrompt";
import {
  useUploadFile,
  useUploadPolicy,
  type FilePurpose,
  type StoredFile,
} from "@/features/storage/api/useStorage";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";

/** Les codes de features de docs/10 §3, écrits une fois. */
const FEATURE_UPLOAD = "storage.upload";
const FEATURE_MAX_FILE_MB = "storage.max_file_mb";

const BYTES_PER_MB = 1024 * 1024;

interface FileUploaderProps {
  purpose: FilePurpose;
  onUploaded: (file: StoredFile) => void;
  label?: string;
}

export function FileUploader({ purpose, onUploaded, label }: FileUploaderProps) {
  const { t } = useTranslation("storage");
  const errorMessage = useApiErrorMessage();
  const entitlements = useEntitlements();
  const policy = useUploadPolicy(purpose);
  const upload = useUploadFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<string | null>(null);

  const maxFileMb = entitlements.limitOf(FEATURE_MAX_FILE_MB);
  const accepted = policy.data?.allowed_content_types ?? [];

  // `has()` et non une lecture de la valeur : sur un quota, `value` vaut `5000`,
  // qui serait « vrai » et ferait passer un quota pour une capacité ouverte.
  if (!entitlements.isPending && !entitlements.has(FEATURE_UPLOAD)) {
    return <UpgradePrompt feature={FEATURE_UPLOAD} />;
  }

  function check(file: File): string | null {
    if (accepted.length > 0 && !accepted.includes(file.type)) {
      return t("errors.type", { accepted: accepted.join(", ") });
    }
    // `null` signifie **illimité**, `undefined` que les droits n'ont pas encore
    // chargé. Les traiter comme « pas de limite » est correct pour le premier et
    // prudent pour le second : le serveur refusera de toute façon, et bloquer un
    // dépôt légitime pendant le chargement serait le pire des deux défauts.
    if (typeof maxFileMb === "number" && file.size > maxFileMb * BYTES_PER_MB) {
      return t("errors.tooLarge", { limitValue: maxFileMb });
    }
    return null;
  }

  function onPick(file: File | undefined): void {
    if (file === undefined) {
      return;
    }
    const problem = check(file);
    setRejected(problem);
    if (problem !== null) {
      return;
    }
    upload.mutate(
      { file, purpose },
      {
        onSuccess: (stored) => {
          onUploaded(stored);
          // Le champ est vidé après un succès : sans cela, choisir **le même**
          // fichier une seconde fois ne déclenche aucun `change`, et l'écran
          // paraît ignorer le clic.
          if (inputRef.current !== null) {
            inputRef.current.value = "";
          }
        },
      },
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        // L'`accept` vient du serveur, jamais d'une constante recopiée ici :
        // une seconde liste divergerait au premier type ajouté et proposerait
        // un format que l'API refuse ensuite.
        accept={accepted.join(",")}
        className="sr-only"
        aria-label={label ?? t("upload.choose")}
        onChange={(event) => {
          onPick(event.target.files?.[0]);
        }}
      />
      <Button
        type="button"
        variant="secondary"
        disabled={upload.isPending}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        {upload.isPending ? t("upload.inProgress") : (label ?? t("upload.choose"))}
      </Button>
      {typeof maxFileMb === "number" && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("upload.hint", { limitValue: maxFileMb })}
        </p>
      )}
      {rejected !== null && <Alert tone="error">{rejected}</Alert>}
      {upload.isError && <Alert tone="error">{errorMessage(upload.error)}</Alert>}
    </div>
  );
}
